/**
 * Minimal toast surface. Used by `lib/memberships-api.ts` (and any wrapper
 * needing user-facing error reporting) to surface 409 / 5xx messages.
 *
 * One queue, one provider, one `<Toaster>` rendered near the app root.
 * `useToast()` returns `(message: string) => void`. E2e specs locate the
 * surface via `data-testid="toast"`.
 */
import {
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface ToastEntry {
  id: number;
  message: string;
}

interface ToastContextValue {
  showToast: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_LIFETIME_MS = 5000;

/**
 * Module-level emitter that api wrappers call without needing a hook.
 * `<ToastProvider>` registers its `showToast` function on mount; the
 * notify helper reads from this slot.
 */
let globalShowToast: ((m: string) => void) | null = null;

export function notifyToast(message: string): void {
  globalShowToast?.(message);
}

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextIdRef = useRef(1);
  const showToast = useCallback((message: string) => {
    const id = nextIdRef.current++;
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_LIFETIME_MS);
  }, []);
  useEffect(() => {
    globalShowToast = showToast;
    return () => {
      globalShowToast = null;
    };
  }, [showToast]);
  const value = useMemo(() => ({ showToast }), [showToast]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster
        toasts={toasts}
        onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))}
      />
    </ToastContext.Provider>
  );
};

export function useToast(): (message: string) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx.showToast;
}

const Toaster: FC<{ toasts: ToastEntry[]; onDismiss: (id: number) => void }> = ({
  toasts,
  onDismiss,
}) => {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        zIndex: 9999,
      }}
    >
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          data-testid="toast"
          onClick={() => onDismiss(t.id)}
          style={{
            background: "#263457",
            color: "#fff",
            padding: "10px 14px",
            borderRadius: 6,
            border: "none",
            fontSize: 13,
            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            boxShadow: "0 6px 16px rgba(0,0,0,.25)",
            cursor: "pointer",
            maxWidth: 360,
            textAlign: "left",
          }}
        >
          {t.message}
        </button>
      ))}
    </div>
  );
};
